import type { AIAgent } from '@app/services/api.types';
import {
    Descriptions,
    Drawer,
    Empty,
    Flex,
    Tabs,
    type DescriptionsProps,
    type DrawerProps,
    type TabsProps,
} from 'antd';
import useAgent from '@app/hooks/agents/useAgent';
import { StyledButton } from '@app/components/button/Button';
import { useNavigate } from 'react-router';
import { url } from '@app/utils/constants';
import ReactMarkdown from 'react-markdown';
import { useState } from 'react';
import { BiLink } from 'react-icons/bi';

export type AgentDrawerProps = DrawerProps & {
    data: AIAgent | null;
};

export const AgentDrawer = ({ data, ...props }: AgentDrawerProps) => {
    const navigate = useNavigate();
    const { data: agentData } = useAgent(
        { id: data?.id || '' },
        {
            enabled: Boolean(data?.id),
        }
    );

    const [activeTab, setActiveTab] = useState<string>('1');
    const detailsItems: DescriptionsProps['items'] = [
        {
            key: '1',
            label: 'Name',
            children: data?.name || '-',
        },
        {
            key: '2',
            label: 'Type',
            children: data?.type_name || '-',
        },
        {
            key: '3',
            label: 'Numbers Assigned to Agent',
            children: data?.numbers_count || 0,
        },
    ];

    const numberItems: DescriptionsProps['items'][] =
        agentData?.numbers?.map((item, index) => [
            {
                key: `brand_${index}`,
                label: 'Brand',
                children: item.brand || '-',
            },
            {
                key: `location_${index}`,
                label: 'Location',
                children: item.location || '-',
            },
            {
                key: `number_${index}`,
                label: 'Agent Number',
                children: item.number || '-',
            },
            {
                key: `failover_${index}`,
                label: 'Failover Number',
                children: item.failover_number || '-',
            },
        ]) || [];

    const renderAssignedNumbersTab = () => {
        if (!numberItems.length) {
            return (
                <Empty description="No numbers assigned" image={Empty.PRESENTED_IMAGE_SIMPLE}>
                    <StyledButton
                        size="small"
                        icon={<BiLink />}
                        onClick={() => {
                            navigate(url.adminPortal.internalAdmin.assignAgent.numbers);
                        }}>
                        Assign Number
                    </StyledButton>
                </Empty>
            );
        }

        return numberItems?.map((items, index) => (
            <Descriptions
                key={index}
                colon={false}
                layout="vertical"
                items={items}
                column={2}
                className="fixed-cols numbers-assigned-item"
            />
        ));
    };

    const renderDetailsTab = () => {
        return (
            <>
                <Descriptions
                    title="Details"
                    colon={false}
                    layout="vertical"
                    items={detailsItems}
                    column={2}
                />
            </>
        );
    };

    const renderPromptTab = () => {
        return (
            <>
                <Descriptions
                    colon={false}
                    layout="vertical"
                    items={[
                        {
                            key: '1',
                            children: (
                                <div className="markdown-body">
                                    <ReactMarkdown>{agentData?.prompt || '-'}</ReactMarkdown>
                                </div>
                            ),
                        },
                    ]}
                />
            </>
        );
    };

    const items: TabsProps['items'] = [
        {
            key: '1',
            label: 'Details',
            children: renderDetailsTab(),
        },
        {
            key: '2',
            label: 'Prompt',
            children: renderPromptTab(),
        },
        {
            key: '3',
            label: 'Numbers Assigned',
            children: renderAssignedNumbersTab(),
        },
    ];

    return (
        <Drawer
            title={
                <>
                    <Flex align="center" justify="space-between" gap={12}>
                        <span>AI Agent Details</span>
                    </Flex>
                    {data?.id ? (
                        <div className="ant-drawer-header-meta">
                            <span>ID: {data?.id}</span>
                        </div>
                    ) : null}
                </>
            }
            size={700}
            closable={{ 'aria-label': 'Close Button' }}
            afterOpenChange={() => {
                if (!props.open) {
                    setActiveTab('1');
                }
            }}
            {...props}>
            <Tabs
                items={items}
                activeKey={activeTab}
                onChange={(key) => setActiveTab(key)}
                className="sticky-tabs"
            />
        </Drawer>
    );
};

export default AgentDrawer;
